import { LeftIcon, RightIcon } from 'foundations-assets';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import styled from 'foundations-theming/utils';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { GRID_BREAKPOINT_SMALL } from '../layout/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const STACKED_LAYOUT = `
  'prev   contents  next'
`;
const STACKED_WITH_PAGINATION_LAYOUT = `
  'prev   contents  next'
  '.      steps     .   '
`;
const RESPONSIVE_WITH_PAGINATION_LAYOUT = `
  'contents   contents    contents'
  'prev       steps       next    '
`;
const Dot = styled.span.withConfig({
  displayName: "Carousel__Dot",
  componentId: "sc-1vy4txt-0"
})(["cursor:pointer;height:15px;width:15px;margin:0 ", ";border-radius:50%;display:inline-block;transition:background-color 0.6s ease;&:not(:last-child){margin-right:2%;}background-color:", ";"], ({
  theme
}) => theme.space['100'], ({
  theme,
  'aria-current': ariaCurrent
}) => ariaCurrent ? theme.color['border-core-pressed'] : theme.color['border-core-default']);
const CarouselPreviousWrapper = styled.div.withConfig({
  displayName: "Carousel__CarouselPreviousWrapper",
  componentId: "sc-1vy4txt-1"
})(["align-self:center;grid-area:prev;"]);
const CarouselNextWrapper = styled.div.withConfig({
  displayName: "Carousel__CarouselNextWrapper",
  componentId: "sc-1vy4txt-2"
})(["align-self:center;grid-area:next;"]);
const CarouselWrapper = styled.section.withConfig({
  displayName: "Carousel__CarouselWrapper",
  componentId: "sc-1vy4txt-3"
})(["display:flex;flex-wrap:wrap;grid-gap:", " ", ";grid-template-areas:", ";grid-template-columns:auto 1fr auto;padding:", ";padding-top:0;position:relative;> *{flex:1 1 100%;}@supports (display:grid){display:grid;}@media only screen and (max-width:", "){grid-template-areas:", ";&& > *{margin-right:0;}}"], ({
  theme
}) => theme.space['400'], ({
  theme
}) => theme.space['500'], props => props.showPagination ? STACKED_WITH_PAGINATION_LAYOUT : STACKED_LAYOUT, ({
  theme
}) => theme.space['500'], GRID_BREAKPOINT_SMALL, props => props.showPagination ? RESPONSIVE_WITH_PAGINATION_LAYOUT : STACKED_LAYOUT);
export default function Carousel({
  cards,
  gutterSize,
  maxCardsPerPage = 1,
  showPagination = true
}) {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * maxCardsPerPage;
  const limit = useMemo(() => cards.length && cards.length <= maxCardsPerPage * page ? cards.length : offset + maxCardsPerPage, [cards.length, maxCardsPerPage, offset, page]);
  const currentCards = useMemo(() => {
    return cards.slice(offset, limit);
  }, [cards, offset, limit]);
  const pageCount = Math.ceil(cards.length / maxCardsPerPage);
  const needsPagination = pageCount > 1;
  const isScrolledToBeginning = page === 1;
  const isScrolledToEnd = page === pageCount;
  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, [setPage]);
  const renderedCarouselSteps = useMemo(() => {
    const steps = [];
    for (let currentPage = 0; currentPage < pageCount; currentPage++) {
      const isCurrentStep = page === currentPage + 1 || undefined;
      steps.push(
      /*#__PURE__*/
      /* @bend-expect trellis-tokenized */
      _jsx(Dot, {
        role: "button",
        "aria-label": `Go to page ${currentPage + 1}`,
        "aria-current": isCurrentStep,
        onClick: () => handlePageChange(currentPage + 1)
      }, currentPage));
    }
    return steps;
  }, [handlePageChange, page, pageCount]);
  return (
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsxs(CarouselWrapper, {
      showPagination: showPagination,
      children: [/*#__PURE__*/_jsx(CardGrid, {
        columns: maxCardsPerPage,
        gutterSize: gutterSize,
        children: currentCards.map((item, index) => /*#__PURE__*/_jsx("div", {
          children: item
        }, index))
      }), needsPagination && /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(CarouselPreviousWrapper, {
          children: /*#__PURE__*/_jsx(IconButton, {
            "aria-label": "previous",
            disabled: isScrolledToBeginning,
            onClick: () => {
              if (page > 1) {
                handlePageChange(page - 1);
              }
            },
            size: "sm",
            children: /*#__PURE__*/_jsx(LeftIcon, {})
          })
        }), /*#__PURE__*/_jsx(CarouselNextWrapper, {
          children: /*#__PURE__*/_jsx(IconButton, {
            "aria-label": "next",
            disabled: isScrolledToEnd,
            onClick: () => {
              if (page < pageCount) {
                handlePageChange(page + 1);
              }
            },
            size: "sm",
            children: /*#__PURE__*/_jsx(RightIcon, {})
          })
        })]
      }), needsPagination && showPagination && /*#__PURE__*/_jsx("div", {
        role: "group",
        "aria-label": "Carousel pagination",
        style: {
          gridArea: 'steps',
          textAlign: 'center'
        },
        children: renderedCarouselSteps
      })]
    })
  );
}